import { Flex, message, Upload } from 'antd';
import { AttachedIcon, CloseCircle, Heading } from '@/components';
import { useTranslation } from 'react-i18next';
import AttachmentItem from '@/components/TicketComment/AttachmentItem';

interface Props {
  disabled?: boolean;
  value?: any[];
  onChange?: (files: any[]) => void;
}

const Uploader = ({ disabled, value = [], onChange }: Props) => {
  const fileList = value;

  const { t } = useTranslation();

  const handleRemoveFile = (uid: string | number) => {
    if (!disabled) {
      const newList = fileList.filter((file) => file.uid !== uid);
      onChange?.(newList);
    }
  };

  return (
    <>
      <Flex gap={'.5rem'} className='text-xl'>
        <Upload
          multiple
          fileList={fileList}
          beforeUpload={(file) => {
            const maxTotalSize = 20480 * 1024;
            const maxFiles = 5;

            if (fileList.length >= maxFiles) {
              message.error(t('attachments_limit_exceeded', { max: maxFiles }));
              return Upload.LIST_IGNORE;
            }

            const currentTotal = fileList.reduce((acc, f) => {
              const size = f.size ?? f.originFileObj?.size ?? 0;
              return acc + size;
            }, 0);

            const newTotal = currentTotal + file.size;
            const remaining = maxTotalSize - newTotal;
            if (newTotal > maxTotalSize) {
              message.error(t('file_exceeds_the_maximum_allowed_size', { fileName: file.name }));
              return Upload.LIST_IGNORE;
            }

            return false;
          }}
          onChange={({ fileList }) => onChange?.(fileList)}
          showUploadList={false}
          disabled={disabled}
        >
          <Flex
            className={`${
              disabled ? 'cursor-not-allowed' : 'cursor-pointer '
            } bg-customGrayShade4 p-2 rounded-full`}
            justify='center'
            align='center'
          >
            <Heading title={t('upload_file')} leadingIcon={<AttachedIcon />} />
          </Flex>
        </Upload>
      </Flex>
      {fileList.length > 0 && (
        <Flex align='center' gap={'1rem'} className='flex-wrap p-2'>
          {fileList.map((file) => (
            <AttachmentItem
              key={file.id}
              file={{
                id: file.uid,
                file_name: file.name,
                content_type: file.type,
                mapped_content_url: URL.createObjectURL(file.originFileObj),
              }}
              onDelete={(id) => handleRemoveFile(id)}
            />
          ))}
        </Flex>
      )}
    </>
  );
};

export default Uploader;
